import { useCallback, useEffect, useState } from "react";

/* ============================================================================
   REDISENO-SMARTMEDIC · FASE 2 (Eje 2 — ancho de la navegación)
   ----------------------------------------------------------------------------
   React pasa a ser la ÚNICA fuente de verdad de `data-sidebar-size` en <body>.

   POR QUÉ EXISTE ESTE HOOK (bug real reportado en pruebas):
   `public/js/app.js` (script del tema Skote) hace, en el evento `load`:

       1024 <= innerWidth && innerWidth <= 1366 && body.setAttribute("data-sidebar-size","sm")

   es decir, en CUALQUIER pantalla de 1024 a 1366px fuerza el menú al modo
   rail de 70px. Y su handler del botón hamburguesa se ata por jQuery a
   `#vertical-menu-btn` durante su init, cuando React todavía no ha montado el
   botón: nunca engancha. Resultado: el menú se quedaba atascado en 70px, con
   las etiquetas del menú React (que no soportaba ese modo) partidas y
   cortadas, y sin forma de salir.

   Aquí se corrige de raíz: el estado vive en React, se re-estampa también en
   `load` (para ganarle al script del tema, que corre después del primer
   render) y el botón alterna de verdad.

   Contrato:
     nav          "lg" (expandido) | "sm" (rail)
     preferencia  la elección del usuario, en localStorage
     < 1280px     se fuerza rail, sin tocar la preferencia guardada -- al
                  volver a una pantalla ancha se recupera lo que el usuario
                  había elegido.
   ========================================================================== */

export const NAV_STORAGE_KEY = "smartmedic.nav";
export const NAV_EVENTO = "smartmedic:nav";
export const NAV_EXPANDIDO = "lg";
export const NAV_RAIL = "sm";
/** Por debajo de este ancho el menú colapsa solo (documento de rediseño §2). */
export const NAV_BREAKPOINT = 1280;
/** Por debajo de esto el tema oculta el menú y usa overlay (`sidebar-enable`). */
export const NAV_BREAKPOINT_MOVIL = 992;

export function normalizarPreferencia(valor) {
    return valor === NAV_RAIL || valor === NAV_EXPANDIDO ? valor : NAV_EXPANDIDO;
}

/** Ancho manda por debajo del breakpoint; por encima manda la preferencia. */
export function resolverNav(preferencia, ancho) {
    if (typeof ancho === "number" && ancho < NAV_BREAKPOINT) return NAV_RAIL;
    return normalizarPreferencia(preferencia);
}

export function leerPreferencia() {
    try {
        if (typeof window === "undefined" || !window.localStorage) return NAV_EXPANDIDO;
        return normalizarPreferencia(window.localStorage.getItem(NAV_STORAGE_KEY));
    } catch (e) {
        return NAV_EXPANDIDO;
    }
}

function guardarPreferencia(valor) {
    try {
        if (typeof window !== "undefined" && window.localStorage) {
            window.localStorage.setItem(NAV_STORAGE_KEY, valor);
        }
    } catch (e) {
        /* modo privado / almacenamiento bloqueado: la preferencia dura la sesión */
    }
    // La preferencia se puede cambiar desde DOS sitios (el botón de la barra
    // superior y Configuración > Apariencia). Sin este aviso, cada instancia
    // del hook se quedaría con su propio valor y el primer `resize` volvería a
    // estampar el de la instancia que no se enteró.
    if (typeof window !== "undefined" && typeof window.dispatchEvent === "function") {
        window.dispatchEvent(new CustomEvent(NAV_EVENTO, { detail: valor }));
    }
}

const anchoActual = () => (typeof window === "undefined" ? NAV_BREAKPOINT : window.innerWidth);

export default function useNavCollapse() {
    const [preferencia, setPreferencia] = useState(leerPreferencia);
    const [nav, setNav] = useState(() => resolverNav(leerPreferencia(), anchoActual()));

    // Estampar en <body> y RE-estampar en `load`: el script del tema corre
    // después del primer render y volvería a poner "sm" por su cuenta.
    useEffect(() => {
        const aplicar = () => {
            if (typeof document !== "undefined") document.body.setAttribute("data-sidebar-size", nav);
        };
        aplicar();
        window.addEventListener("load", aplicar);
        return () => window.removeEventListener("load", aplicar);
    }, [nav]);

    // El ancho de la ventana sólo fuerza el rail; nunca pisa la preferencia.
    useEffect(() => {
        const alRedimensionar = () => setNav(resolverNav(preferencia, window.innerWidth));
        window.addEventListener("resize", alRedimensionar);
        return () => window.removeEventListener("resize", alRedimensionar);
    }, [preferencia]);

    useEffect(() => {
        setNav(resolverNav(preferencia, anchoActual()));
    }, [preferencia]);

    // Otra instancia del hook cambió la preferencia: aquí se adopta.
    useEffect(() => {
        const alCambiar = (e) => setPreferencia(normalizarPreferencia(e.detail));
        window.addEventListener(NAV_EVENTO, alCambiar);
        return () => window.removeEventListener(NAV_EVENTO, alCambiar);
    }, []);

    const alternarNav = useCallback(() => {
        setPreferencia((actual) => {
            const siguiente = normalizarPreferencia(actual) === NAV_RAIL ? NAV_EXPANDIDO : NAV_RAIL;
            guardarPreferencia(siguiente);
            return siguiente;
        });
        // Por debajo del breakpoint el ancho manda, pero el usuario acaba de
        // pedir el cambio explícitamente: se respeta hasta el próximo resize.
        setNav((actual) => (actual === NAV_RAIL ? NAV_EXPANDIDO : NAV_RAIL));
    }, []);

    return { nav, preferencia, alternarNav, esRail: nav === NAV_RAIL };
}
